import * as React from "react";
import { useContext, useMemo } from "react";
import { PluginStore } from "graylog-web-plugin/plugin";
import moment from "moment";
import capitalize from "lodash/capitalize";
import toLower from "lodash/toLower";
import Timestamp from "components/common/Timestamp";
import usePluginEntities from "hooks/usePluginEntities";
import { DEFAULT_TIMERANGE } from "views/Constants";
import StreamsContext from "contexts/StreamsContext";
import { MESSAGE_LIST_WIDGET_LIMIT } from "common/components/widgetRenderers/Constants";
export const FallbackWidgetDescription = ({ error }) => /* @__PURE__ */ React.createElement("span", null, "Error generating widget description:", /* @__PURE__ */ React.createElement("br", null), /* @__PURE__ */ React.createElement("code", null, error.toString()));
const assertUnreachable = (_ignored) => {
  throw new Error("Didn't expect to get here");
};
const isRelativeTimeRangeStartOnly = (timerange) => "range" in timerange;
const humanizeDuration = (seconds) => moment.duration(seconds, "seconds").format("d [days] h [hours] m [minutes] s [seconds]", { trim: "all", useToLocaleString: false });
const formatRelativeRange = (relativeRange) => {
  const start = humanizeDuration(
    isRelativeTimeRangeStartOnly(relativeRange) ? relativeRange.range : relativeRange.from
  );
  const end = isRelativeTimeRangeStartOnly(relativeRange) || !relativeRange.to ? "now" : `${humanizeDuration(relativeRange.to)} ago`;
  return /* @__PURE__ */ React.createElement("span", null, "from ", start, " ago until ", end);
};
const TimeRangeDescription = ({ timeRange: timeRangeProp }) => {
  const timeRange = timeRangeProp != null ? timeRangeProp : DEFAULT_TIMERANGE;
  switch (timeRange.type) {
    case "relative":
      return formatRelativeRange(timeRange);
    case "absolute":
      return /* @__PURE__ */ React.createElement("span", null, "from ", /* @__PURE__ */ React.createElement(Timestamp, { dateTime: timeRange.from }), " to ", /* @__PURE__ */ React.createElement(Timestamp, { dateTime: timeRange.to }));
    case "keyword":
      return /* @__PURE__ */ React.createElement("span", null, "in the ", toLower(timeRange.keyword));
    default:
      assertUnreachable(timeRange);
  }
  return assertUnreachable(timeRange);
};
const StreamsDescription = ({ streams }) => {
  if (!streams || streams.length === 0) {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, "all messages");
  }
  const usedStreams = streams.map((s) => /* @__PURE__ */ React.createElement("em", { key: s.id }, s.title)).reduce((acc, current, idx) => {
    if (streams.length === 1) {
      return current;
    }
    if (idx === streams.length - 1) {
      return /* @__PURE__ */ React.createElement(React.Fragment, null, acc, "and ", current);
    }
    return /* @__PURE__ */ React.createElement(React.Fragment, null, acc, current, ",", " ");
  });
  return /* @__PURE__ */ React.createElement(React.Fragment, null, "messages in ", streams.length === 1 ? "stream" : "streams", " ", usedStreams);
};
const CalculatedAt = ({ date }) => /* @__PURE__ */ React.createElement(Timestamp, { dateTime: date });
const _visualizationDescription = (config) => {
  const visualization = PluginStore.exports("visualizationTypes").filter((viz) => viz.type === config.visualization)[0];
  if (!visualization) {
    throw new Error(`Unable to find visualization component for type: ${config.visualization}`);
  }
  return visualization.displayName;
};
const WidgetTypeDescription = ({ widgetPlugin, config }) => {
  switch (widgetPlugin.type) {
    case "AGGREGATION":
      return /* @__PURE__ */ React.createElement(React.Fragment, null, capitalize(_visualizationDescription(config)), " ", toLower(widgetPlugin.titleGenerator({ config })));
    case "MESSAGES":
      return /* @__PURE__ */ React.createElement(React.Fragment, null, `Message list (limited to ${MESSAGE_LIST_WIDGET_LIMIT} messages)`);
    default:
      return /* @__PURE__ */ React.createElement(React.Fragment, null, "Unknown widget");
  }
};
const QueryDescription = ({ query }) => {
  const { query_string: queryString = "*" } = query != null ? query : {};
  return /* @__PURE__ */ React.createElement("em", null, queryString);
};
const FiltersDescription = ({ filters }) => /* @__PURE__ */ React.createElement(React.Fragment, null, filters.map(({ queryString, negation }, index) => {
  const prefix = `${index > 0 ? " AND " : ""}`;
  const query = `${negation ? "NOT " : ""}(${queryString})`;
  return `${prefix}${query}`;
}));
const WidgetDescription = ({
  calculatedAt = /* @__PURE__ */ new Date(),
  widgetConfig,
  widgetQuery,
  widgetFilters,
  widgetStreams: widgetStreamsProp,
  widgetTimerange,
  widgetType,
  hideQuery
}) => {
  var _a;
  const streams = (_a = useContext(StreamsContext)) != null ? _a : [];
  const widgetStreams = streams.filter((stream) => widgetStreamsProp == null ? void 0 : widgetStreamsProp.includes(stream.id));
  const widgetTypes = usePluginEntities("enterpriseWidgets");
  const widgetPlugin = useMemo(
    () => widgetTypes.find((w) => w.type.toUpperCase() === widgetType.toUpperCase()),
    [widgetType, widgetTypes]
  );
  if (!widgetPlugin) {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, "No description available.");
  }
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(WidgetTypeDescription, { widgetPlugin, config: widgetConfig }), " for", " ", /* @__PURE__ */ React.createElement(StreamsDescription, { streams: widgetStreams }), " ", /* @__PURE__ */ React.createElement(TimeRangeDescription, { timeRange: widgetTimerange }), ".", !hideQuery && /* @__PURE__ */ React.createElement(React.Fragment, null, " ", "The visualization represents data for query ", /* @__PURE__ */ React.createElement(QueryDescription, { query: widgetQuery }), widgetFilters && widgetFilters.length > 0 && /* @__PURE__ */ React.createElement(React.Fragment, null, " ", "and filter queries", " ", /* @__PURE__ */ React.createElement("em", null, /* @__PURE__ */ React.createElement(FiltersDescription, { filters: widgetFilters }))), "."), " ", "Calculated at ", /* @__PURE__ */ React.createElement(CalculatedAt, { date: calculatedAt }), ".");
};
export default WidgetDescription;
